import React, {useEffect, useState} from 'react';
import createAxiosInstance from '../../../support/AxiosSupport';
import {archetypeLabel} from '../../../support/strategyLabels';
import Spinner from '../../Spinner';
import {choiceCardClass} from '../constants';
import {StepProps, stringField} from './types';

interface StrategyArchetype {
    code: string;
    defaultTargetSize: number | null;
    defaultDeadStockThresholdDays: number | null;
    defaultAutopilotLevel: string | null;
}

/** Пояснения архетипов: в API их нет, а выбор вслепую по одному слову бесполезен. */
const ARCHETYPE_HINTS: Record<string, string> = {
    MINIMALIST: 'Меньше вещей, больше пространства. Только то, что действительно носите.',
    CAPSULE: 'Универсальные вещи, которые сочетаются между собой.',
    BUSINESS: 'Офисный гардероб с акцентом на деловой вид.',
    BUDGET: 'Максимум пользы при минимуме затрат.',
    SPORTS: 'Функциональный гардероб для активного образа жизни.',
    FASHION: 'Тренды и эксперименты со стилем.',
    CUSTOM: 'Свои правила: параметры стратегии зададите сами.',
};

/**
 * Шаг «Стратегия гардероба» (слот 3). Архетипы берём из того же источника, что и страница
 * стратегии (`GET /api/strategy/archetypes`), подписи — из `support/strategyLabels`: свой
 * список внутри мастера разошёлся бы с продуктом.
 */
const StepStrategy: React.FC<StepProps> = ({value, onChange}) => {
    const [archetypes, setArchetypes] = useState<StrategyArchetype[]>([]);
    const [loading, setLoading] = useState(true);
    const [failed, setFailed] = useState(false);
    const selected = stringField(value, 'archetype');

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const api = await createAxiosInstance();
                const res = await api.get<StrategyArchetype[]>('/strategy/archetypes');
                if (!cancelled) {
                    setArchetypes(res.data);
                }
            } catch {
                if (!cancelled) {
                    setFailed(true);
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    if (loading) {
        return <Spinner/>;
    }

    if (failed || archetypes.length === 0) {
        return (
            <p className="text-sm text-gray-500 dark:text-gray-400" data-testid="onboarding-strategy-unavailable">
                Список стратегий сейчас недоступен — пропустите шаг, стратегию можно выбрать позже
                на странице «Стратегия».
            </p>
        );
    }

    return (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="onboarding-step-strategy">
            {archetypes.map(a => (
                <button
                    key={a.code}
                    type="button"
                    onClick={() => onChange({archetype: a.code})}
                    aria-pressed={selected === a.code}
                    className={choiceCardClass(selected === a.code)}
                    data-testid={`onboarding-archetype-${a.code}`}
                >
                    <span className="block font-semibold">{archetypeLabel(a.code)}</span>
                    <span className={`block mt-1 text-xs ${
                        selected === a.code ? 'text-white/80' : 'text-gray-500 dark:text-gray-400'
                    }`}>
                        {ARCHETYPE_HINTS[a.code] ?? ARCHETYPE_HINTS.CUSTOM}
                    </span>
                </button>
            ))}
        </div>
    );
};

export default StepStrategy;
